import type { SidepanelSurfaceVariant } from '../../profile/sidepanel-profile'

/**
 * The Meeting View's tabs (HIG-6354). `brief` prepares you for the meeting,
 * `transcript` is the record of it, and `recap` is what came out of it: the For
 * You follow-ups over the summary. The `tabs` layout adds `notepad`, which
 * holds your own notes, and `chat`, which holds the meeting's thread.
 */
export type MeetingTab = 'brief' | 'transcript' | 'recap' | 'notepad' | 'chat'

/** Recording or finished, from `MeetingViewModel.variant`. */
export type MeetingVariant = 'live' | 'past'

/** How the Meeting View is laid out, from the sidepanel's `meetingView` surface. */
export type MeetingViewLayout = SidepanelSurfaceVariant<'meetingView'>

/**
 * Whether the meeting has tabs, which it does once it's being transcribed or
 * finished. Before that the brief is the whole body.
 */
export function hasMeetingTabs(model: { variant: MeetingVariant; audioNoteId: string | null }): boolean {
  return model.variant === 'live' || !!model.audioNoteId
}

const CLASSIC_MEETING_TABS: ReadonlyArray<MeetingTab> = ['brief', 'transcript', 'recap']

const MEETING_TABS: ReadonlyArray<MeetingTab> = ['brief', 'notepad', 'transcript', 'recap', 'chat']

/** The tabs a layout's bar shows, in order. A Custom Meeting has no brief to offer, so it gets no empty tab. */
export function listMeetingTabs(layout: MeetingViewLayout, hasBriefTab: boolean): ReadonlyArray<MeetingTab> {
  const tabs = layout === 'tabs' ? MEETING_TABS : CLASSIC_MEETING_TABS
  return hasBriefTab ? tabs : tabs.filter((tab) => tab !== 'brief')
}

export function isMeetingTab(value: unknown): value is MeetingTab {
  return typeof value === 'string' && (MEETING_TABS as ReadonlyArray<string>).includes(value)
}

/**
 * Stamp a tab with the variant it was chosen under before storing it.
 *
 * Recording and finished are different documents, so a tab chosen for one
 * expires when the meeting becomes the other. Stamping keeps that a pure
 * derivation instead of an effect watching for the transition: the router
 * renders only the top entry, so a view sitting under an Action View (or under
 * a closed panel) is unmounted at the moment its meeting ends and would miss
 * the edge entirely.
 */
export function stampMeetingTab(tab: MeetingTab, variant: MeetingVariant): string {
  return `${variant}:${tab}`
}

/**
 * The pick an opener's ask is recorded as. Before recording, Brief is the only
 * content rather than a tab choice, so the entry is left to land on the recap
 * once the meeting has one.
 */
export function stampOpenerAsk(ask: MeetingTab, variant: MeetingVariant, hasRecording: boolean): string {
  return variant === 'past' && !hasRecording ? stampMeetingTab('recap', 'past') : stampMeetingTab(ask, variant)
}

/** The stored pick, or `undefined` once the meeting has outlived it. */
export function readMeetingTabStamp(stored: string | undefined, variant: MeetingVariant): MeetingTab | undefined {
  if (stored === undefined) return undefined
  const separator = stored.indexOf(':')
  if (separator === -1 || stored.slice(0, separator) !== variant) return undefined
  const tab = stored.slice(separator + 1)
  return isMeetingTab(tab) ? tab : undefined
}

export interface ResolveMeetingTabInput {
  /** This entry's stamped pick, if it has ever chosen a tab. */
  stored: string | undefined
  variant: MeetingVariant
  /**
   * Where the opener asked to land (`openBrief`, a Home row's recap shortcut).
   * Present only until `useMeetingTab` records it, so it is always this open's ask.
   */
  initialTab: MeetingTab | undefined
  /** A Custom Meeting has no brief to offer, so it renders no Brief tab. */
  hasBriefTab: boolean
}

/**
 * Which tab the view shows. While recording the transcript is the meeting.
 * Once it ends, the recap is why you came back.
 *
 * An opener's ask lands, even over an earlier pick: every open carries one, and
 * a reopen collapses onto the existing entry. `useMeetingTab` then records it as
 * a stamped pick and clears it, so an ask made for a live meeting doesn't
 * outlive the recording.
 *
 * A Brief pick with no tab to show it falls through to the lifecycle default
 * rather than an empty panel.
 */
export function resolveMeetingTab({ stored, variant, initialTab, hasBriefTab }: ResolveMeetingTabInput): MeetingTab {
  const lifecycleDefault: MeetingTab = variant === 'live' ? 'transcript' : 'recap'
  const candidate = initialTab ?? readMeetingTabStamp(stored, variant) ?? lifecycleDefault
  return candidate === 'brief' && !hasBriefTab ? lifecycleDefault : candidate
}
